import { useState } from 'react';
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';
import {
  getDetailedMonthlyOpexTotal,
  hasDetailedMonthlyOpex,
} from '@/features/investments/form/utils.ts';
import { formatCurrency } from '@/utils/misc.ts';

type OpexMode = 'simple' | 'detailed';

const ExpensesSection = () => {
  const { control } = useFormContext<InvestmentFormData>();

  const [
    monthlyOpexTotal,
    ibiTax,
    communityFee,
    supplyCosts,
    maintenanceCosts,
    wasteTax,
    homeInsurance,
    rentInsurance,
    otherOpex,
  ] = useWatch({
    control,
    name: [
      'monthlyOpexTotal',
      'ibiTax',
      'communityFee',
      'supplyCosts',
      'maintenanceCosts',
      'wasteTax',
      'homeInsurance',
      'rentInsurance',
      'otherOpex',
    ],
  });

  const hasDetailed = hasDetailedMonthlyOpex({
    ibiTax,
    communityFee,
    supplyCosts,
    maintenanceCosts,
    wasteTax,
    homeInsurance,
    rentInsurance,
    otherOpex,
  });

  const [opexMode, setOpexMode] = useState<OpexMode>(hasDetailed ? 'detailed' : 'simple');

  const detailedMonthlyOpex = getDetailedMonthlyOpexTotal({
    ibiTax,
    communityFee,
    supplyCosts,
    maintenanceCosts,
    wasteTax,
    homeInsurance,
    rentInsurance,
    otherOpex,
  });

  const displayedMonthlyOpex =
    opexMode === 'detailed' ? detailedMonthlyOpex : ((monthlyOpexTotal ?? 0) as number);

  return (
    <FormSection title="5. Gastos" description="Coste operativo mensual y su posible desglose.">
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600}>
            Gastos operativos mensuales
          </Typography>
          <ToggleButtonGroup
            value={opexMode}
            exclusive
            onChange={(_, value: OpexMode | null) => {
              if (value) setOpexMode(value);
            }}
            size="small"
          >
            <ToggleButton value="simple" sx={{ px: 2, py: 0.5, textTransform: 'none' }}>
              Total
            </ToggleButton>
            <ToggleButton value="detailed" sx={{ px: 2, py: 0.5, textTransform: 'none' }}>
              Desglose
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {opexMode === 'simple' ? (
          <Controller
            name="monthlyOpexTotal"
            control={control}
            render={({ field: { value, onChange } }) => (
              <EurCurrencyField
                label="Gasto operativo mensual total"
                value={value}
                onChange={onChange}
                helperText="Opcional"
              />
            )}
          />
        ) : (
          <Stack spacing={2}>
            <Controller
              name="ibiTax"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="IBI (anual)" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="communityFee"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Cuota de comunidad (mensual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="supplyCosts"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField label="Suministros (mensual)" value={value} onChange={onChange} />
              )}
            />
            <Controller
              name="maintenanceCosts"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Mantenimiento (mensual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="wasteTax"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Tasa de basura (anual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="homeInsurance"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Seguro de hogar (anual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="rentInsurance"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Seguro de alquiler (anual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="otherOpex"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EurCurrencyField
                  label="Otros gastos (mensual)"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Stack>
        )}
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} color="primary.main">
        Total mensual estimado: {formatCurrency(displayedMonthlyOpex ?? 0)}
      </Typography>
    </FormSection>
  );
};

export default ExpensesSection;
